import React from 'react';
import { RatingGroup } from '@ark-ui/react/rating-group';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface RatingGroupInputProps extends BaseInputProps<number> {
  /** Počet hvězd. */
  count?: number;
  /** Povolit půlhvězdy. */
  allowHalf?: boolean;
  className?: string;
}

/**
 * Atomické hvězdičkové hodnocení postavené na Ark UI `RatingGroup`.
 *
 * `ids.label` míří na popisek z `<FormField />` — zag z něj odvodí
 * `aria-labelledby` radiogroup controlu. `ids.hiddenInput` mapuje `id`
 * z FormFieldu bez přímého přepisu `id` na elementu (to by rozbilo zag
 * `getElementById` lookupy).
 */
export const RatingGroupInput: React.FC<RatingGroupInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  describedBy,
  labelId,
  testAttributes,
  count = 5,
  allowHalf,
  className,
}) => (
  <RatingGroup.Root
    name={name}
    ids={{ hiddenInput: id, label: labelId }}
    value={value ?? 0}
    count={count}
    allowHalf={allowHalf}
    disabled={disabled}
    readOnly={readOnly}
    onValueChange={(details) => onChange(details.value)}
    onBlur={onBlur}
    className={clsx(className)}
    data-invalid={hasError ? '' : undefined}
    aria-describedby={describedBy}
  >
    <RatingGroup.Control className={styles.ratingControl}>
      <RatingGroup.Context>
        {({ items }) =>
          items.map((item) => (
            <RatingGroup.Item key={item} index={item} className={styles.ratingItem}>
              <Icon name="star" size={24} className={styles.ratingStarBg} />
              <Icon name="star" size={24} className={styles.ratingStarFg} />
            </RatingGroup.Item>
          ))
        }
      </RatingGroup.Context>
      <RatingGroup.HiddenInput ref={ref} {...testAttributes} />
    </RatingGroup.Control>
  </RatingGroup.Root>
);

RatingGroupInput.displayName = 'RatingGroupInput';
